import { AssessmentService, ServerService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AwsMigrationStrategyAssessment, DeserializedServer, 
    ExcludedServers, MigrationStrategySummary, TcoAssessment 
} from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Container, SpaceBetween, Spinner, Tabs } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { getId } from '../../common/arn';
import { HelpPanelTopicOptions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { MatchParam } from '../../common/types';
import { deepCopy } from '../../common/util';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { TcoContext } from '../../contexts/TcoContext';
import { ErrorMsgFormat } from '../tco/error-handler-context/CommonTcoErrorHandler';
import { generateExcludedServersErrorMsgs } from '../tco/error-handler-context/helper';
import GenerateErrorMessageBox from '../tco/error-handler-context/TcoErrorHandler';
import { migrationStrategyResourceName } from './constants';
import { CalculatedStrategy, ServerWithMigrationStrategy } from './interfaces';
import { calculateStrategy, isOverridden } from './migration-helper';
import MigrationStrategyApplicationTable from './MigrationStrategyApplicationTable';
import MigrationStrategyCount from './MigrationStrategyCount';
import MigrationStrategyServerTable from './MigrationStrategyServerTable';

type Props = FlashContextInterface;

const assessmentService = new AssessmentService();
const tcoService = new TcoService();
const serverService = new ServerService();

function MigrationStrategy({ flash }: Props) {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const portfolioId = match?.params?.portfolioId;
    const tcoAssessmentId = match?.params?.tcoAssessmentId ?? null;
    const isMounted = useIsMounted();
    const [ migrationAssessmentLoading, setMigrationAssessmentLoading ] = useState(false);
    const [ migrationStrategyAssessment, setMigrationStrategyAssessment ] = useState<AwsMigrationStrategyAssessment | null>(null);
    const [ excludedServers, setExcludedServers ] = useState<ExcludedServers>({outOfScope: 0});
    const [ scoredServers, setScoredServers ] = useState<ServerWithMigrationStrategy[]>([]);
    const [ migrationStrategyAssessmentId, setMigrationStrategyAssessmentId ] = useState<string | null>(null);
    const [ showRecalculateAlert, setShowRecalculateAlert ] = useState(false);
    const [ showInfoAlert, setShowInfoAlert ] = useState(true);
    const { panelHandler } = useContext(HelpPanelContext);

    const { assessment, assessmentToUpdate, tcoLoading, updateAssessmentToUpdate } = useContext(TcoContext);
    const history = useHistory();

    const loadMigrationAssessment = async () => {
        setMigrationAssessmentLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const migrationAssessment = await tcoService.getAwsMigrationStrategy(tcoAssessmentId);
                if (isMounted()) {
                    setMigrationStrategyAssessment(migrationAssessment);
                    if (migrationAssessment._references) {
                        const id = getId(migrationAssessment._references, migrationStrategyResourceName);
                        setMigrationStrategyAssessmentId(id);
                    }
                }
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, '');
            }
        } finally {
            if (isMounted()) {
                setMigrationAssessmentLoading(false);
            }
        }
    };

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.MIGRATION_STRATEGY);
    }, [  panelHandler  ]);

    useEffect(() => {
        if (tcoLoading) {
            setShowRecalculateAlert(false);
        }
    }, [ tcoLoading ]);

    useEffect(() => {
        if (!assessment) {
            return;
        }
        const loadExcludedServers = async () => {
            try {
                if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                    const summary = await tcoService.getSummary(tcoAssessmentId);
                    setExcludedServers(summary.output.excludedServers);
                }
            } catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, (err as Error).message, '');
                }
            }
        };
        if (assessment) {
            loadMigrationAssessment();
            loadExcludedServers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    useEffect(() => {
        const loadScoredServers = async () => {
            if (!portfolioId) {
                return;
            }
            const migrationStrategy = migrationStrategyAssessment?.output.migrationStrategy;
            const servers = await serverService.list(portfolioId);
            const scoredServers: ServerWithMigrationStrategy[] = servers
                .reduce((inScopeServers: ServerWithMigrationStrategy[], server: DeserializedServer) => {
                    const serverMigrationStrategy = (migrationStrategy && server.serverId) ? migrationStrategy[server.serverId] : null;
                    if (server.inScope) {
                        const isOverriddenValue: boolean = isOverridden(server, serverMigrationStrategy);
                        const strategy: CalculatedStrategy = calculateStrategy(server, serverMigrationStrategy);
                        const serverScore = {
                            id: server.serverId,
                            migrationStrategy: strategy.value,
                            isOverridden: isOverriddenValue,
                            recommendedTools: (strategy.displayTools && serverMigrationStrategy?.recommendedTools) 
                                ? serverMigrationStrategy?.recommendedTools : '',
                        };
                        inScopeServers.push({...serverScore, ...server});
                    }
                    return inScopeServers;
                }, []);
            setScoredServers(scoredServers);
        };

        loadScoredServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ migrationStrategyAssessment ]);

    const refreshMigrationAssessmentAfterUpdate = async () => {
        setShowRecalculateAlert(true);
        await loadMigrationAssessment();
        if (portfolioId && tcoAssessmentId) {
            await assessmentService.updateScoresSummary(portfolioId, tcoAssessmentId);
        }
    };

    const updateServerStrategyCountOverride = (isOverride: boolean) => {
        const updatedAssessment: TcoAssessment = deepCopy(assessmentToUpdate);
        updatedAssessment.input.migration.serverStrategyCountOverride = isOverride;
        updateAssessmentToUpdate(updatedAssessment);
    };

    const updateDatabaseStrategyCountOverride = (isOverride: boolean) => {
        const updatedAssessment: TcoAssessment = deepCopy(assessmentToUpdate);
        updatedAssessment.input.migration.databaseStrategyCountOverride = isOverride;
        updateAssessmentToUpdate(updatedAssessment);
    };

    const updateServerInputSummary = (updatedInputSummary: MigrationStrategySummary[]) => {
        const updatedAssessment: TcoAssessment = deepCopy(assessmentToUpdate);
        updatedAssessment.input.migration.migrationStrategySummary = updatedInputSummary;
        updateAssessmentToUpdate(updatedAssessment);
    };

    const updateDatabaseInputSummary = (updatedInputSummary: MigrationStrategySummary[]) => {
        const updatedAssessment: TcoAssessment = deepCopy(assessmentToUpdate);
        updatedAssessment.input.migration.databaseMigrationStrategySummary = updatedInputSummary;
        updateAssessmentToUpdate(updatedAssessment);
    };

    const tabs = (tcoLoading || migrationAssessmentLoading) ? [] : ([
        {
            label: constants.SERVER_MIGRATION_STRATEGY,
            id: 'serverMigrationStrategy',
            content:  migrationStrategyAssessment && assessmentToUpdate ? (
                <SpaceBetween size='m'> 
                    <Container>
                        <MigrationStrategyCount 
                            serverMigrationStrategySummary={migrationStrategyAssessment?.output?.migration?.migrationStrategySummaryForPortfolio ?? []}
                            serverInputSummary={assessmentToUpdate.input?.migration?.migrationStrategySummary ?? []}
                            databaseMigrationStrategySummary={migrationStrategyAssessment?.output?.migration?.databaseMigrationStrategySummaryForPortfolio
                                ?? []}
                            databaseInputSummary={assessmentToUpdate.input?.migration?.databaseMigrationStrategySummary ?? []}
                            serverStrategyCountOverride={assessmentToUpdate.input?.migration?.serverStrategyCountOverride ?? false}
                            databaseStrategyCountOverride={assessmentToUpdate.input?.migration?.databaseStrategyCountOverride ?? false}
                            updateDatabaseStrategyCountOverride={updateDatabaseStrategyCountOverride}
                            updateServerStrategyCountOverride={updateServerStrategyCountOverride}
                            updateDatabaseInputSummary={updateDatabaseInputSummary}
                            updateServerInputSummary={updateServerInputSummary}
                        />
                    </Container>
                    <Container>
                        <MigrationStrategyServerTable 
                            scoredServers={scoredServers}
                            onMigrationOverride={refreshMigrationAssessmentAfterUpdate}
                            migrationStrategyAssessmentId={migrationStrategyAssessmentId}
                            showRecalculateAlert={showRecalculateAlert}
                        />
                    </Container>
                </SpaceBetween>
            ) : null
        },
        {
            label: constants.APPLICATION_MIGRATION_STRATEGY,
            id: 'applicationMigrationStrategy',
            content: (
                <Container>
                    <MigrationStrategyApplicationTable
                        scoredServers={scoredServers}
                    />
                </Container>
            )
        }
    ]);

    const content = migrationStrategyAssessment?.output.migration ? (
        <Tabs tabs={tabs}/>
    ) : (
        <Alert type='warning'>{constants.RECALCULATE_PORTFOLIO_WARNING}</Alert>
    );

    const errorMessages: ErrorMsgFormat[] = portfolioId ? generateExcludedServersErrorMsgs(excludedServers, portfolioId) : [];

    return (
        <Container>
            {migrationAssessmentLoading || tcoLoading ? (
                <Spinner size='large'/>
            ):(
                <SpaceBetween size='s'>
                    { showInfoAlert ? (
                        <Alert
                            data-testid='infoAlert'
                            type='info'
                            dismissible
                            onDismiss={() => setShowInfoAlert(false)}
                        >
                            {constants.MIGRATION_STRATEGY_INFO_P1}<br/><br/>
                            <em>
                                {constants.MIGRATION_STRATEGY_INFO_P2_START} <strong>{constants.RELOCATE}</strong> 
                                {constants.MIGRATION_STRATEGY_INFO_P2_MIDDLE} <strong>{constants.RELOCATE}</strong>
                                {constants.MIGRATION_STRATEGY_INFO_P2_END}
                            </em>
                        </Alert>
                    ) : null }
                    <GenerateErrorMessageBox
                        errorMessages={errorMessages}
                        history={history}
                    />
                    {migrationStrategyAssessment?.output.warningMsg ? (
                        <Alert>
                            {migrationStrategyAssessment.output.warningMsg}
                        </Alert>
                    ) : null}
                    {content}
                </SpaceBetween>
            )}
        </Container>
    );
}

export default withFlashContext(MigrationStrategy);
